import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Check, AlertCircle, Star, Clock } from 'lucide-react';
import { api, API } from '../lib/api';
import { trackFormStart } from '../lib/tracking';
import { getAttribution } from '../lib/utm';
import { CATEGORIES, CITIES } from '../data/categories';

export default function LeadForm({
  initialCategory = '',
  initialTreatment = '',
  initialClinic = '',
  initialCity = '',
  clinics = [],
  compact = false,
  lockedTreatment = false,
  ctaText = 'Send forespørsel →',
  onSuccess,
}) {
  const navigate = useNavigate();
  const [city, setCity] = useState(initialCity);
  const [category, setCategory] = useState(initialCategory);
  const [treatment, setTreatment] = useState(initialTreatment);
  const [clinicId, setClinicId] = useState(initialClinic);
  const [timeframe, setTimeframe] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('+47 ');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (initialCategory) setCategory(initialCategory);
    if (initialTreatment) setTreatment(initialTreatment);
    if (initialCity) setCity(initialCity);
    if (initialClinic) setClinicId(initialClinic);
  }, [initialCategory, initialTreatment, initialCity, initialClinic]);

  // If category changes, clear treatment if it doesn't belong.
  // Skip when treatment is locked from a landing page — the value is owned by the parent.
  useEffect(() => {
    if (lockedTreatment) return;
    if (category) {
      const c = CATEGORIES.find((x) => x.name === category);
      if (c && !c.treatments.includes(treatment)) {
        setTreatment('');
      }
    }
  }, [category]);  // eslint-disable-line

  const treatments = useMemo(() => {
    if (!category) return [];
    const c = CATEGORIES.find((x) => x.name === category);
    return c ? c.treatments : [];
  }, [category]);

  const filteredClinics = useMemo(() => {
    if (!clinics?.length) return [];
    let list = clinics.filter((c) => {
      const matchesCategory = !category || c.categories.includes(category);
      const matchesTreatment = !treatment || c.treatments.includes(treatment);
      const matchesCity = !city || c.city === city || city === 'Annet';
      return matchesCategory && matchesTreatment && matchesCity;
    });
    // featured first
    list = [...list].sort((a, b) => {
      if (a.placeholder && !b.placeholder) return 1;
      if (!a.placeholder && b.placeholder) return -1;
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return (b.rating || 0) - (a.rating || 0);
    });
    return list;
  }, [clinics, category, treatment, city]);

  const onAnyFocus = () => {
    if (touched) return;
    setTouched(true);
    trackFormStart(category);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!consent) { setError('Du må samtykke for å sende forespørsel.'); return; }
    if (!name || !phone || !email || !category || !treatment) {
      setError('Alle påkrevde felt må fylles ut.'); return;
    }
    setLoading(true);
    const clinicObj = clinics.find((c) => c.id === clinicId);
    const clinicName = clinicObj ? clinicObj.name : '';
    try {
      const res = await fetch(`${API}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          phone,
          category,
          treatment,
          city: city || null,
          clinic_selected: clinicName || null,
          message: message || null,
          timeframe: timeframe || null,
          consent: true,
          attribution: getAttribution() || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || `Serverfeil (${res.status}). Prøv igjen.`);
        setLoading(false);
        return;
      }
      if (onSuccess) onSuccess();
      const dest = clinicName
        ? `/takk?klinikk=${encodeURIComponent(clinicName)}`
        : `/takk`;
      navigate(dest);
    } catch (err) {
      console.error('Lead submit error:', err);
      setError('Kunne ikke nå serveren. Sjekk internett og prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#FAF8F5] border border-transparent focus:border-[#0A1F44] focus:ring-0 rounded-lg px-4 py-3 text-[#0A1F44] placeholder-[#64748B] transition-all focus:bg-white focus:outline-none text-[15px]";
  const labelCls = "block text-xs font-semibold uppercase tracking-[0.15em] text-[#0A1F44]/70 mb-1.5";

  return (
    <div data-testid="lead-form-card" className={`bg-white rounded-2xl border border-[#0A1F44]/10 shadow-[0_20px_60px_-20px_rgba(10,31,68,0.25)] ${compact ? 'p-6' : 'p-7 md:p-8'}`}>
      <div className="flex items-start gap-3 mb-6">
        <div className="h-11 w-11 rounded-full bg-[#3B82F6]/10 flex items-center justify-center flex-shrink-0">
          <Check className="text-[#3B82F6]" size={20} strokeWidth={2} />
        </div>
        <div>
          <h3 className="font-display text-2xl font-bold text-[#0A1F44] leading-tight">Finn din spesialist</h3>
          <p className="text-sm text-[#64748B] mt-0.5">Klinikken kontakter deg innen 24 timer</p>
        </div>
      </div>

      <form onSubmit={submit} onFocusCapture={onAnyFocus} className="space-y-3.5">
        <div>
          <label className={labelCls}>Velg by</label>
          <select data-testid="form-city" value={city} onChange={(e) => setCity(e.target.value)} className={inputCls}>
            <option value="">Velg by…</option>
            {CITIES.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
        </div>

        {lockedTreatment && treatment ? (
          <div data-testid="form-treatment-locked" className="rounded-lg bg-[#3B82F6]/5 border border-[#3B82F6]/20 px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#0A1F44]/70 mb-0.5">Behandling</div>
              <div className="text-[#0A1F44] font-semibold">{treatment}</div>
            </div>
            <Check className="text-[#3B82F6]" size={18} />
          </div>
        ) : (
          <>
            <div>
              <label className={labelCls}>Behandling *</label>
              <select data-testid="form-category" value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls} required>
                <option value="">Velg kategori…</option>
                {CATEGORIES.map((c) => (<option key={c.slug} value={c.name}>{c.name}</option>))}
              </select>
            </div>

            {category && (
              <div>
                <label className={labelCls}>Spesifikk behandling *</label>
                <select data-testid="form-treatment" value={treatment} onChange={(e) => setTreatment(e.target.value)} className={inputCls} required>
                  <option value="">Velg behandling…</option>
                  {treatments.map((t) => (<option key={t} value={t}>{t}</option>))}
                </select>
              </div>
            )}
          </>
        )}

        {treatment && filteredClinics.length > 0 && (
          <div>
            <label className={labelCls}>Velg klinikk</label>
            <select data-testid="form-clinic" value={clinicId} onChange={(e) => setClinicId(e.target.value)} className={inputCls}>
              <option value="">La oss foreslå en for deg</option>
              {filteredClinics.map((c) => (
                <option key={c.id} value={c.id} disabled={c.placeholder}>
                  {c.placeholder ? `${c.name} — Kommer snart` : `⭐ ${c.rating} — ${c.name} — ${c.waitTime}`}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className={labelCls}>Når ønsker du behandling? *</label>
          <select data-testid="form-timeframe" value={timeframe} onChange={(e) => setTimeframe(e.target.value)} className={inputCls} required>
            <option value="">Velg tidsramme…</option>
            <option value="Snarest mulig">Snarest mulig</option>
            <option value="Innen 1 uke">Innen 1 uke</option>
            <option value="Innen 14 dager">Innen 14 dager</option>
            <option value="Innen 1 måned">Innen 1 måned</option>
            <option value="Innen 3 måneder">Innen 3 måneder</option>
            <option value="Bare å undersøke prisene">Bare å undersøke prisene</option>
          </select>
        </div>

        <div>
          <label className={labelCls}>Fullt navn *</label>
          <input data-testid="form-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Kari Nordmann" className={inputCls} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Telefon *</label>
            <input data-testid="form-phone" type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+47 xxx xx xxx" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>E-post *</label>
            <input data-testid="form-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="du@epost.no" className={inputCls} />
          </div>
        </div>

        <div>
          <label className={labelCls}>Kort beskrivelse (valgfritt)</label>
          <textarea data-testid="form-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={2} placeholder="Fortell kort hva du ønsker…" className={inputCls + ' resize-none'} />
        </div>

        <label className="flex items-start gap-3 text-xs text-[#64748B] leading-relaxed pt-1 cursor-pointer">
          <input
            data-testid="form-consent"
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-[#3B82F6] flex-shrink-0"
            required
          />
          <span>
            Jeg samtykker til at Specialists.no deler mine opplysninger med valgt klinikk for å motta et tilbud. Les vår <a href="/personvern" target="_blank" rel="noreferrer" className="underline text-[#0A1F44]">personvernerklæring</a>.
          </span>
        </label>

        {error && (
          <div data-testid="form-error" className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2.5 rounded-lg">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        <button
          data-testid="form-submit"
          type="submit"
          disabled={loading}
          className="w-full bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-70 text-white py-4 rounded-lg font-semibold transition-all text-[15px] shadow-md hover:shadow-lg flex items-center justify-center gap-2"
        >
          {loading ? (<><Loader2 className="animate-spin" size={18} /> Sender…</>) : ctaText}
        </button>

        <div className="flex items-center justify-center gap-5 pt-1 text-[11px] text-[#64748B]">
          <span className="flex items-center gap-1"><Star size={12} className="text-[#3B82F6]" fill="#3B82F6" /> 4+ stjerner</span>
          <span className="flex items-center gap-1"><Clock size={12} /> Svar innen 24t</span>
          <span>Gratis & uforpliktende</span>
        </div>
      </form>
    </div>
  );
}
